/* ============================================================================
   SLURPPED — motion init
   ----------------------------------------------------------------------------
   Makes GSAP "ready for motion" and runs the shared scroll-reveal system.
   Sets brand-standard defaults (custom easing, snappy durations) and a global
   prefers-reduced-motion flag that section code MUST respect.

   Design & Motion Standard: transform/opacity only, custom easing (never
   linear), durations < 500ms (mobile 350ms), 2-3 strategic moments per section,
   reduced-motion respected. Defer-loaded after gsap.
   ========================================================================== */
/* ---- Noodle squiggles draw themselves (UX review, 30 Sep 2026) ----------
   Independent of GSAP on purpose, and placed FIRST so it arms every squiggle
   before the main block below releases the .s-motion pre-hide (no flash of a
   finished line). IntersectionObserver, never ScrollTrigger: Horizon scrolls an
   inner wrapper on desktop. CSS: slurpped-foundation.css, "The noodle pulls itself". */
(function () {
  'use strict';
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (typeof IntersectionObserver === 'undefined') return;
  function arm() {
    var lines = document.querySelectorAll('.s-squiggle:not(.is-armed)');
    if (!lines.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-drawn');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    // If the 3s fail-safe already showed everything (slow connection), a line
    // that is on screen stays drawn rather than vanishing and redrawing.
    var released = !document.documentElement.classList.contains('s-motion');
    var vh = window.innerHeight || document.documentElement.clientHeight;
    for (var i = 0; i < lines.length; i++) {
      if (released && lines[i].getBoundingClientRect().top < vh) continue;
      lines[i].classList.add('is-armed');
      io.observe(lines[i]);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arm);
  else arm();
})();

/* ---- Box drawings start cooking (UX review, 30 Sep 2026) -----------------
   .s-cook tiles get .is-cooking only while at least a third of them is on
   screen, so the loops (CSS, slurpped-foundation.css) stop off screen. */
(function () {
  'use strict';
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (typeof IntersectionObserver === 'undefined') return;
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { e.target.classList.toggle('is-cooking', e.isIntersecting); });
  }, { threshold: 0.35 });
  function scan(root) {
    var tiles = (root || document).querySelectorAll('.s-cook');
    for (var i = 0; i < tiles.length; i++) io.observe(tiles[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { scan(); });
  else scan();
  // Theme editor re-renders a section in place; pick up its new tiles.
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
})();

(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Public namespace so every section reads the same brand motion config.
  var Slurpped = (window.Slurpped = window.Slurpped || {});
  Slurpped.reduceMotion = reduceMotion;

  // Hand-off from the pre-paint hide (.s-motion, set in the foundation snippet):
  // once our inline GSAP styles own the hiding — or anything is missing — the
  // CSS class comes off so content can never be stranded invisible.
  function releasePreHide() {
    document.documentElement.classList.remove('s-motion');
  }

  if (typeof window.gsap === 'undefined') {
    // GSAP failed to load (offline / blocked CDN / SRI mismatch). Fail safe:
    // sections must treat motion as optional enhancement, content stays visible.
    Slurpped.motionReady = false;
    releasePreHide();
    return;
  }

  // Brand defaults: premium ease-out, snappy. (power3.out ~= ease-out-expo feel.)
  // 500ms is the Design & Motion Standard's hard ceiling.
  gsap.defaults({
    ease: 'power3.out',
    duration: reduceMotion ? 0 : 0.5
  });

  // Mobile gets snappier reveals per the Standard (350ms vs 450ms).
  var isMobile = window.matchMedia('(max-width: 749px)').matches;
  var revealDuration = isMobile ? 0.35 : 0.45;

  // Honour the OS setting globally: if reduced motion, neutralise GSAP output
  // (durations to 0) so any section animation resolves instantly to its end state.
  if (reduceMotion) {
    gsap.globalTimeline.timeScale(100);
  }

  Slurpped.motionReady = true;

  // Reveal trigger = IntersectionObserver (NOT ScrollTrigger). IO observes
  // intersection with the browser viewport directly, so it works no matter which
  // element actually scrolls — Horizon scrolls an inner .page-wrapper on desktop,
  // which ScrollTrigger's window scroller never saw. Same staggered fade+rise look.
  var io = null;
  function getObserver() {
    if (io) return io;
    if (typeof IntersectionObserver === 'undefined') return null;
    io = new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          playReveal(entry.target);
          observer.unobserve(entry.target); // once
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0 }
    );
    return io;
  }

  function revealItems(container) {
    var items = container.querySelectorAll('[data-s-reveal-item]');
    return items.length ? items : [container];
  }

  // Staggered fade+rise to the visible end state, then clear inline styles.
  function playReveal(container) {
    var stagger = parseFloat(container.getAttribute('data-s-reveal-stagger'));
    if (isNaN(stagger)) stagger = 0.06;
    gsap.to(revealItems(container), {
      opacity: 1,
      y: 0,
      duration: revealDuration,
      ease: 'power3.out',
      stagger: stagger,
      clearProps: 'all'
    });
  }

  // Register a container: hide its items (JS-only) then reveal on viewport enter.
  // Public so sections/future code can opt in imperatively too.
  Slurpped.reveal = function (container) {
    // Reduced motion, no GSAP, or no IO support -> leave content visible, no hiding.
    if (reduceMotion) return;
    var observer = getObserver();
    if (!observer) return;
    var y = parseInt(container.getAttribute('data-s-reveal-y'), 10);
    if (isNaN(y)) y = 24;
    gsap.set(revealItems(container), { opacity: 0, y: y });
    observer.observe(container);
  };

  // Auto-scanner: any [data-s-reveal] element becomes a stagger container; its
  // [data-s-reveal-item] descendants enter in sequence when it hits the viewport.
  // Content is only hidden once this runs, so if JS/GSAP fail it stays visible.
  function initReveals() {
    // If the 3s fail-safe already dropped the pre-hide (a slow connection),
    // everything has been visible for a while — never hide what the visitor
    // may be looking at, or has already scrolled past.
    var released = !document.documentElement.classList.contains('s-motion');
    var vh = window.innerHeight || document.documentElement.clientHeight;
    document.querySelectorAll('[data-s-reveal]').forEach(function (container) {
      // First-screen blocks already played their entrance in CSS the moment
      // the page parsed (layout/theme.liquid + slurpped-foundation.css,
      // 23 Sep 2026 speed audit). GSAP must not re-hide them.
      if (container.hasAttribute('data-s-reveal-now')) return;
      if (released && container.getBoundingClientRect().top < vh) return;
      Slurpped.reveal(container);
    });
    // Inline styles now carry the hiding (set synchronously above) — release
    // the CSS pre-hide in the same tick so there's no gap and no flash.
    releasePreHide();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReveals);
  } else {
    initReveals();
  }
})();
